import { z } from "zod/v4";

/**
 * A store's brand styling, as measured from its rendered storefront.
 *
 * This mirrors `StyleGuideLite` from `@intelligems/ig-embedded-preview`, which
 * is what the extractor actually returns. It is restated here rather than
 * imported because `ig-types` sits at the base of the dependency graph with no
 * workspace dependencies, and an API contract needs a runtime schema anyway —
 * the handler validates responses against it. `ig-clients` asserts at compile
 * time that the two agree, so drift fails the build rather than a request.
 */
export const StyleGuideTextStyleSchema = z.object({
  family: z.string().nullable(),
  sizePx: z.number().nullable(),
  weight: z.number().nullable(),
  color: z.string().nullable(),
});

/**
 * A downloadable face behind one of the families above.
 *
 * `loadedFonts` can only ever be names: it is read from `document.fonts`, and
 * a `FontFace` exposes no `src`. Anything drawing a type specimen from a name
 * alone is drawing its own fallback under the store's label. These come from
 * the CSSOM and are what make the real face renderable.
 *
 * `src` is the merchant-origin URL. Fetch it through the reverse proxy's
 * `/__ASSET__/` passthrough (`toAssetProxyUrl` in the frontend) — the proxy
 * replaces the upstream's CORS headers with its own, so a font host that
 * allows no cross-origin reads still works.
 */
export const StyleGuideFontFaceSchema = z.object({
  family: z.string(),
  src: z.string(),
  weight: z.string().nullable(),
  style: z.string().nullable(),
});

/**
 * One style in the store's type scale, with how much of the page uses it.
 *
 * `heading`, `subheading` and `body` are three slots and a storefront's type
 * system does not fit in three — a store can set section headings in one face
 * and display lines in another, and neither is more "the" heading. The scale
 * reports what is there; which of it to use is the consumer's call.
 *
 * `chars` alone cannot categorize: a display face carries a handful of words
 * against thousands in the body face, so by volume it all but disappears.
 * `tag` is what separates them.
 */
export const StyleGuideTypeScaleEntrySchema = z.object({
  family: z.string().nullable(),
  sizePx: z.number().nullable(),
  weight: z.number().nullable(),
  color: z.string().nullable(),
  /** Characters of visible text set in this style. */
  chars: z.number(),
  /** The element it is set on, or its nearest heading ancestor where it has one. */
  tag: z.string().nullable(),
});

export const StyleGuideButtonStyleSchema = z.object({
  background: z.string().nullable(),
  foreground: z.string().nullable(),
  borderColor: z.string().nullable(),
  radiusPx: z.number().nullable(),
});

export const StyleGuideSchema = z.object({
  /**
   * Counts how many fields came back non-null — it is a completeness measure,
   * not a statement about whether the values are right. Do not surface it to a
   * merchant as certainty.
   */
  confidence: z.enum(["high", "medium", "low"]),
  colors: z.object({
    background: z.string().nullable(),
    surface: z.string().nullable(),
    accent: z.string().nullable(),
    border: z.string().nullable(),
    palette: z.array(z.string()),
  }),
  typography: z.object({
    heading: StyleGuideTextStyleSchema.nullable(),
    subheading: StyleGuideTextStyleSchema.nullable(),
    body: StyleGuideTextStyleSchema.nullable(),
    loadedFonts: z.array(z.string()),
    /**
     * Optional because guides extracted before this field existed are cached
     * for the life of a conversation, and a stored briefing must not stop
     * validating because we started reading more.
     */
    fontFaces: z.array(StyleGuideFontFaceSchema).optional().default([]),
    /**
     * Optional for the same reason as `fontFaces`: a guide cached before this
     * field existed must keep validating. Undeclared keys are stripped on the
     * way through, so anything reading the scale needs it declared here.
     */
    scale: z.array(StyleGuideTypeScaleEntrySchema).optional().default([]),
  }),
  buttons: z.object({
    primary: StyleGuideButtonStyleSchema.nullable(),
    secondary: StyleGuideButtonStyleSchema.nullable(),
  }),
});

export const StoreStyleGuideRequestBodySchema = z.object({
  /**
   * Render this instead of the organization's own storefront. For checking a
   * store we have not installed on, and for reproducing a reported result.
   * Omitted, the organization's customer-facing domain is used.
   */
  url: z.string().optional(),
  /**
   * Render even if a stored guide is still fresh.
   *
   * For a merchant who has just changed their theme and wants the panel to
   * catch up, and for reproducing a result. Ordinary callers omit it: a render
   * costs a headless browser, and the whole point of storing the guide is that
   * nobody pays for one twice.
   */
  refresh: z.boolean().optional(),
});

export const StoreStyleGuideResponseBodySchema = z.object({
  /** The URL actually rendered, so a surprising result can be reproduced. */
  url: z.string(),
  /**
   * When the render behind this guide happened.
   *
   * Present whether the guide was rendered now or read from storage, because a
   * caller cannot otherwise tell how old the styling it is showing is. A
   * merchant asking "why are these the wrong colours" is asking this.
   */
  fetchedAt: z.string(),
  /**
   * Null when the page loaded but our extractor was not on it — a page the
   * proxy did not process. That is a different failure from a store whose
   * styling we could not read, and the caller should treat it differently.
   */
  styleGuide: StyleGuideSchema.nullable(),
});

export type StyleGuideType = z.infer<typeof StyleGuideSchema>;
export type StyleGuideFontFaceType = z.infer<typeof StyleGuideFontFaceSchema>;
export type StoreStyleGuideRequestBodyType = z.infer<
  typeof StoreStyleGuideRequestBodySchema
>;
export type StoreStyleGuideResponseBodyType = z.infer<
  typeof StoreStyleGuideResponseBodySchema
>;
